import React, { useEffect, useMemo, useState } from 'react';
import { PersistedWorkflow } from '@cotera/sdk/core';
import { Text } from '@cotera/client/app/components/ui';
import { Combobox } from '@cotera/client/v0/components/ui/combobox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@cotera/client/v0/components/ui/select';
import { useToolCallUpstreamSamples } from '../../../hooks/use-tool-call-upstream-samples';
import {
  describeTimezoneOffset,
  formatInTimezone,
  formatRelativeToNow,
  listSelectableTimezones,
  resolveViewerTimezone,
} from '../../../debounce-timezone';
import { asRecord, readString } from '../../utils';
import { describeFieldPath } from '../strategies/describe-field-path';
import { Field } from './editor-field';
import { buildUpstreamSources } from './variable-picker-data';
import {
  UpstreamRefChip,
  UpstreamRefPicker,
  UpstreamRefPickerButton,
} from './upstream-ref-picker';
import type { WorkflowNodeEditorProps } from './types';

const {
  DEBOUNCE_WINDOWS,
  DEBOUNCE_WINDOW_LABELS,
  DEBOUNCE_WEEK_STARTS,
  DEFAULT_DEBOUNCE_WINDOW,
  DEFAULT_DEBOUNCE_WEEK_START,
  computeNextDebounceBucketStart,
  isDebounceWindow,
  isDebounceWeekStart,
  isValidDebounceTimezone,
} = PersistedWorkflow;

type DebounceWindow = PersistedWorkflow.DebounceWindow;
type DebounceWeekStart = PersistedWorkflow.DebounceWeekStart;

const WEEK_START_LABELS: Record<DebounceWeekStart, string> = {
  monday: 'Monday',
  sunday: 'Sunday',
};

/**
 * Config editor for a debounce node: which field identifies the thing, how long
 * to stay quiet for it, and which clock the boundary is measured on.
 *
 * The preview at the bottom is the point of the layout rather than filler. A
 * debounce is the one node whose behaviour you cannot read off its settings —
 * "once a week" hides whether that means a calendar week or a rolling seven
 * days, and the timezone silently moves the boundary by up to a day. Showing
 * the resolved boundary in the node's own zone answers both while you configure
 * it, instead of the first time it fires at the wrong hour.
 *
 * Reports `null` while the key is unset: without one the node cannot decide
 * anything, and saving it would fail the step at run time instead of here.
 */
export const DebounceNodeEditor: React.FC<WorkflowNodeEditorProps> = ({
  node,
  onChange,
}) => {
  const data = asRecord(node.data);
  const readWindow = (
    source: Record<string, unknown> | null
  ): DebounceWindow => {
    const raw = source?.['window'];
    return isDebounceWindow(raw) ? raw : DEFAULT_DEBOUNCE_WINDOW;
  };
  const readWeekStart = (
    source: Record<string, unknown> | null
  ): DebounceWeekStart => {
    const raw = source?.['weekStartsOn'];
    return isDebounceWeekStart(raw) ? raw : DEFAULT_DEBOUNCE_WEEK_START;
  };
  const readTimezone = (source: Record<string, unknown> | null): string => {
    const raw = source?.['timezone'];
    return isValidDebounceTimezone(raw) ? raw : resolveViewerTimezone();
  };

  const [keyPath, setKeyPath] = useState<string>(
    readString(data, 'keyPath') ?? ''
  );
  const [window, setWindow] = useState<DebounceWindow>(readWindow(data));
  const [weekStartsOn, setWeekStartsOn] = useState<DebounceWeekStart>(
    readWeekStart(data)
  );
  const [timezone, setTimezone] = useState<string>(readTimezone(data));

  // Re-seed when the drawer is pointed at a different node, matching the other
  // editors' render-phase reset rather than an effect.
  const [seededNodeId, setSeededNodeId] = useState(node.id);
  if (seededNodeId !== node.id) {
    setSeededNodeId(node.id);
    const next = asRecord(node.data);
    setKeyPath(readString(next, 'keyPath') ?? '');
    setWindow(readWindow(next));
    setWeekStartsOn(readWeekStart(next));
    setTimezone(readTimezone(next));
  }

  const { upstreamSamples } = useToolCallUpstreamSamples(node.id);
  const sources = useMemo(
    () => buildUpstreamSources(upstreamSamples),
    [upstreamSamples]
  );
  const sourceLabels = useMemo(
    () =>
      new Map(
        upstreamSamples.map(
          (sample) => [sample.sourceName, sample.label] as const
        )
      ),
    [upstreamSamples]
  );

  const timezoneItems = useMemo(
    () =>
      listSelectableTimezones().map((zone) => {
        const offset = describeTimezoneOffset(zone);
        return {
          value: zone,
          label: offset === null ? zone : `${zone} (${offset})`,
        };
      }),
    []
  );

  useEffect(() => {
    const trimmedKey = keyPath.trim();
    if (trimmedKey === '') {
      onChange(null);
      return;
    }
    onChange({
      type: 'debounce',
      data: {
        keyPath: trimmedKey,
        window,
        timezone,
        // Only meaningful for a week window, but always written so switching
        // back to weekly restores the choice instead of silently resetting it.
        weekStartsOn,
      },
    });
  }, [keyPath, window, timezone, weekStartsOn, onChange]);

  // Pinned at mount so the absolute date and the relative distance are derived
  // from one instant, and so the preview does not shift under the reader on
  // every unrelated re-render.
  const [previewNow] = useState(() => new Date());
  const preview = useMemo(() => {
    const config = { keyPath: keyPath.trim(), window, timezone, weekStartsOn };
    try {
      const reopens = computeNextDebounceBucketStart({
        now: previewNow,
        config,
      });
      return {
        reopens,
        relative: formatRelativeToNow(reopens, previewNow),
      };
    } catch {
      return null;
    }
  }, [previewNow, keyPath, window, timezone, weekStartsOn]);

  const keyLabel =
    keyPath.trim() === ''
      ? 'each key'
      : describeFieldPath(keyPath, sourceLabels);

  return (
    <div className="flex flex-col gap-5">
      <Field label="Key" hint="The field identifying what to debounce.">
        <div className="flex items-center gap-2">
          {keyPath.trim() !== '' ? (
            <UpstreamRefChip
              refValue={keyPath}
              sourceLabels={sourceLabels}
              onRemove={() => setKeyPath('')}
            />
          ) : (
            <UpstreamRefPicker sources={sources} onPick={setKeyPath}>
              <button
                type="button"
                className="border-input bg-background hover:bg-muted/60 text-muted-foreground flex h-9 flex-1 items-center justify-start rounded-md border px-3 text-left text-sm"
              >
                {sources.length > 0
                  ? 'Select a field…'
                  : 'No upstream fields yet'}
              </button>
            </UpstreamRefPicker>
          )}
          {keyPath.trim() !== '' ? (
            <UpstreamRefPickerButton
              sources={sources}
              onPick={setKeyPath}
              title="Change field"
            />
          ) : null}
        </div>
      </Field>

      {/* Window and week start are one idea — "once a week, from Monday" — so
          they share a row. Every other window has no second control, and a
          half-width select beside dead space reads as a missing field, so the
          row collapses to one column instead. */}
      <div
        className={
          window === 'week' ? 'grid grid-cols-2 gap-3' : 'grid grid-cols-1'
        }
      >
        <Field label="Let through once per">
          <Select
            value={window}
            onValueChange={(next) => {
              if (isDebounceWindow(next)) {
                setWindow(next);
              }
            }}
          >
            <SelectTrigger className="h-9 w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {DEBOUNCE_WINDOWS.map((option) => (
                <SelectItem key={option} value={option}>
                  {DEBOUNCE_WINDOW_LABELS[option]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>

        {window === 'week' ? (
          <Field label="Starting on">
            <Select
              value={weekStartsOn}
              onValueChange={(next) => {
                if (isDebounceWeekStart(next)) {
                  setWeekStartsOn(next);
                }
              }}
            >
              <SelectTrigger className="h-9 w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {DEBOUNCE_WEEK_STARTS.map((option) => (
                  <SelectItem key={option} value={option}>
                    {WEEK_START_LABELS[option]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
        ) : null}
      </div>

      <Field
        label="Timezone"
        hint="Where the window boundary falls. Stored with the node, so it stays put wherever the workflow runs."
      >
        <Combobox
          items={timezoneItems}
          value={timezone}
          onChange={(next) => {
            if (isValidDebounceTimezone(next)) {
              setTimezone(next);
            }
          }}
          placeholder="Select a timezone…"
          searchPlaceholder="Search timezones…"
          emptyMessage="No matching timezone."
          className="h-9 w-full"
          // Zone names run long ("America/Argentina/Buenos_Aires (GMT-03:00)"),
          // and the default 200px dropdown truncates them to the point of being
          // unpickable.
          contentClassName="w-[--radix-popover-trigger-width] min-w-[280px]"
        />
      </Field>

      {preview !== null ? (
        // One fact, stated once. The earlier version was a two-row table, but
        // nobody needs to know when the current window *opened* — the only
        // question a debounce raises is when the thing can happen again, so
        // that answer is the whole box and everything else supports it.
        <div className="border-input bg-muted/30 flex flex-col gap-3 rounded-md border p-3">
          <div className="flex flex-col gap-0.5">
            <Text.Caption className="text-muted-foreground block">
              A value passing now goes quiet until
            </Text.Caption>
            <Text.Span className="text-foreground text-base font-medium tabular-nums">
              {formatInTimezone(preview.reopens, timezone)}
            </Text.Span>
            <Text.Caption className="text-muted-foreground block">
              {[preview.relative, timezone]
                .filter((part): part is string => part !== null)
                .join(' · ')}
            </Text.Caption>
          </div>
          <Text.Caption className="text-muted-foreground/80 block border-t pt-2">
            Calendar windows, not rolling. Each {keyLabel} gets its own.
          </Text.Caption>
        </div>
      ) : null}
    </div>
  );
};
